import { colors } from '../constants/colors';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export function DataDeletionPage() {
  useDocumentTitle('Delete Your Data');

  return (
    <div style={{ flex: 1, padding: '40px 24px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', fontWeight: '700', color: colors.ink.black, marginBottom: '8px' }}>Delete Your Data</h1>
      <p style={{ color: colors.gray[600], fontSize: '14px', marginBottom: '32px' }}>
        Snagg, LLC ("Snagg," "we," "us," or "our") operates the Snagg platform and Snagg iOS and Android apps. We respect your right to control your personal data.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', color: colors.gray[900], fontSize: '15px', lineHeight: '1.7' }}>
        <section style={{ padding: '20px', backgroundColor: colors.gray[100], borderRadius: '12px' }}>
          <h2 style={sectionTitle}>Delete Your Account in the App</h2>
          <p style={{ margin: 0 }}>
            The fastest way to delete your account is directly in the Snagg mobile app: open{' '}
            <strong>Account</strong>, scroll to the bottom, and tap <strong>Delete Account</strong>, then confirm.
            This permanently deletes your account and removes your personal information right away — no email
            required. (See <strong>What We May Retain</strong> below for the limited records we keep for legal
            and tax reasons.)
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>Request Deletion by Email</h2>
          <p>
            If you can't access the app, you can also request the deletion of your personal data by email. Send
            an email to{' '}
            <a href="mailto:privacy@snagg.live" style={{ color: colors.brand.blue, textDecoration: 'none', fontWeight: 600 }}>
              privacy@snagg.live
            </a>{' '}
            with the subject line <strong>"Data Deletion Request"</strong>.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>What to Include</h2>
          <p>Please include the following in your email so we can verify your identity and process your request:</p>
          <ul style={listStyle}>
            <li>The email address associated with your Snagg account.</li>
            <li>Your Snagg username.</li>
            <li>Whether you want a <strong>full account deletion</strong> or deletion of <strong>specific data</strong> (e.g., shipping addresses, payment methods, messages).</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>What Happens Next</h2>
          <ul style={listStyle}>
            <li>We will verify your identity and confirm receipt of your request within <strong>3 business days</strong>.</li>
            <li>Your data will be deleted or anonymized within <strong>30 days</strong> of verification.</li>
            <li>You will receive a confirmation email once the deletion is complete.</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>What We May Retain</h2>
          <p>
            Certain data may be retained after deletion where required by law or for legitimate business purposes, including:
          </p>
          <ul style={listStyle}>
            <li>Transaction records required for tax and accounting compliance.</li>
            <li>Information necessary to resolve ongoing disputes or enforce our Terms of Service.</li>
            <li>Anonymized or aggregated data that can no longer identify you.</li>
          </ul>
        </section>

        <section style={{ padding: '20px', backgroundColor: colors.gray[100], borderRadius: '12px' }}>
          <p style={{ margin: 0 }}>
            For more details on how we handle your data, see our{' '}
            <Link to="/privacy" style={{ color: colors.brand.blue, textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}

const sectionTitle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#000000',
  marginBottom: '8px',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};
